import type { Vec3 } from './math.ts';

/**
 * Renderer-agnostic level geometry. Three.js builds meshes from this and Rapier
 * builds colliders from the same array, so visuals and collision can never
 * drift apart. `gen_level.py` emits this shape as JSON.
 */
export interface Box {
  /** Centre. */
  pos: Vec3;
  /** Full extents, not half-extents. */
  size: Vec3;
  /**
   * What this box is, which decides how it collides and how it is drawn.
   *
   * `prop` is the one that is not architecture. A crate or a bookshelf blocks
   * movement and sight like a wall does, but it is a *thing* rather than a
   * surface — so the box stays authoritative for collision and the renderer is
   * free to draw a model in its place instead of a textured slab. That split is
   * why crates do not need to be modelled before the level is playable, and why
   * modelling them later changes nothing in core.
   */
  kind: 'floor' | 'wall' | 'ceiling' | 'platform' | 'pillar' | 'prop';
  /**
   * Turn about the vertical, radians. Zero for the overwhelming majority.
   *
   * Yaw only, deliberately. Everything in an arena is a slab standing on the
   * floor, and admitting pitch or roll would mean the navigation grid could no
   * longer treat a box's footprint as a rectangle at all — for a ramp, which is
   * a feature nothing in the game needs yet and one that a `platform` at a step
   * height already covers.
   *
   * A hallway cut at forty-five degrees is what this exists for. Squaring one
   * onto the axes is the difference between a building and a spreadsheet.
   */
  yaw?: number;
  /** Room this belongs to, for lighting and minimap grouping. */
  room?: string;
}

/**
 * A light fixture on a wall, and which way it faces.
 *
 * `yaw` points away from whatever the fixture is mounted on. Blockouts place
 * torches as bare markers with no rotation — a marker in Godot has an identity
 * basis until somebody turns it, and turning a hundred of them by hand is not a
 * reasonable thing to ask — so the converter infers it from the nearest wall
 * instead. That is a guess, but it is a good one: a torch is on a wall, and the
 * direction away from the wall nearest it is where the room is.
 */
export interface Torch {
  pos: Vec3;
  /** Radians. Zero means facing −Z, the same convention as everything else. */
  yaw: number;
}

/**
 * A named piece of floor, and what shape of place it is.
 *
 * The kind is the part that could not be recovered from geometry. A doorway is a
 * chokepoint whatever its measurements — somewhere a fight is decided by who
 * gets there first — and a corridor is somewhere you cannot dodge. Both are
 * facts about how a fight *there* will go, and a bot that knows them can hold a
 * door instead of merely walking through it.
 *
 * Several areas share a `room`: `Cloister01` and `Cloister02` are one place, and
 * a room's doorways are separate areas *of* it, told apart by `label`.
 */
export interface RoomArea {
  room: string;
  kind: 'room' | 'doorway' | 'corridor' | 'gallery';
  label: string;
  pos: Vec3;
  size: Vec3;
  yaw?: number;
}

export interface LevelGeometry {
  name: string;
  boxes: Box[];
  /** Ambient fill, 0..1. Low enough to feel dark, high enough to read walls. */
  ambient: number;
  /**
   * Torch brightness in candela. Physical falloff means this needs to be in the
   * tens, not single digits — irradiance at distance d is roughly value / d².
   */
  torchIntensity: number;
  /** Metres at which a torch reaches zero. */
  torchRange: number;
  torches: Torch[];
  /**
   * Cool overhead light for spaces open to the sky — a cloister, a courtyard.
   *
   * Separate from torches because it is a different *kind* of light and behaves
   * differently: it never gutters, it is never in the budget that torches
   * compete for, and there are one or two of them rather than a hundred.
   */
  moonlights?: Torch[];
  /** Named floor plates. Absent in a gray-box, which has no rooms to name. */
  areas?: RoomArea[];
  /** Optional per-arena grade. Sensible dark defaults are used when absent. */
  lighting?: LightingSettings;
}

/**
 * The knobs an arena tunes its own mood with.
 *
 * Deliberately data rather than code, because this is the layer that most wants
 * playtesting and least wants a rebuild to try a number. It is also the layer
 * that decides whether a bright asset pack survives contact — a desaturated,
 * fogged, torch-lit room reads nothing like a neutral one, whatever is standing
 * in it.
 */
export interface LightingSettings {
  /** Colour beyond the fog, and the colour of the fog itself. */
  skyColor?: number;
  fogColor?: number;
  /** Exponential density. Higher hides more and flattens distance. */
  fogDensity?: number;
  /** Cold fill from above, so silhouettes read without lighting the floor. */
  moonColor?: number;
  moonIntensity?: number;
  /** Warm bounce from below, standing in for light the torches throw around. */
  bounceColor?: number;
  bounceIntensity?: number;
  torchColor?: number;
  /**
   * How far a torch's brightness wanders, 0..1, and how fast. Flicker is the
   * cheapest thing on this list and the one that most stops a dark room reading
   * as a still image.
   */
  torchFlicker?: number;
  torchFlickerSpeed?: number;
  /** Whether the moon casts. One shadow-casting light is the whole budget. */
  shadows?: boolean;
  /** Camera exposure. The final say over everything above. */
  exposure?: number;
}

/**
 * Rotate a world point into a box's own frame.
 *
 * Once a point is local, every test that follows is the axis-aligned one it
 * always was — which is why supporting angled walls costs a rotation at the
 * entrance and no change at all to the arithmetic behind it. The same trick, in
 * the same shape, as `toLocal` for spell walls in `placed.ts`.
 */
export function toBoxLocal(b: Box, p: Vec3): Vec3 {
  const dx = p.x - b.pos.x;
  const dy = p.y - b.pos.y;
  const dz = p.z - b.pos.z;
  if (!b.yaw) return { x: dx, y: dy, z: dz };
  /*
   * The inverse of Three.js's rotation about Y, which is what actually draws the
   * box and what Rapier is given for its collider.
   *
   * This used to negate the angle first and then rotate the same way, which is
   * the inverse of the *other* handedness — so the navigation grid rasterised
   * every angled wall as its own mirror image. It went unnoticed because the
   * converter had a second sign error that cancelled it: nav came out right by
   * accident and only the rendering and the physics were wrong. Fixing either
   * alone would have broken the arena.
   */
  const c = Math.cos(b.yaw);
  const s = Math.sin(b.yaw);
  return { x: dx * c - dz * s, y: dy, z: dx * s + dz * c };
}

/**
 * The axis-aligned footprint an angled box needs, half-extents.
 *
 * A wall turned forty-five degrees occupies a wider rectangle than its own
 * dimensions, and anything working in world axes — grid bounds, broad-phase
 * rejection — has to allow for that or it will clip the corners off.
 */
export function boxFootprint(b: Box): { x: number; z: number } {
  if (!b.yaw) return { x: b.size.x / 2, z: b.size.z / 2 };
  const c = Math.abs(Math.cos(b.yaw));
  const s = Math.abs(Math.sin(b.yaw));
  return {
    x: (b.size.x * c + b.size.z * s) / 2,
    z: (b.size.x * s + b.size.z * c) / 2,
  };
}

export const box = (
  x: number, y: number, z: number,
  w: number, h: number, d: number,
  kind: Box['kind'], room?: string, yaw = 0,
): Box => ({ pos: { x, y, z }, size: { x: w, y: h, z: d }, kind, room, yaw });


// ─────────────────────────────────────────────────────────────────────────────
//  SKY LIDS
// ─────────────────────────────────────────────────────────────────────────────

/** Sampling pitch for finding open sky, metres. Well under a body's width. */
const LID_CELL = 0.5;
/** How far a lid overlaps the roof round its hole, so sampling cannot leave a gap. */
const LID_MARGIN = 0.5;
const LID_THICKNESS = 0.4;
/** How far round a hole to look for the roof it belongs to. */
const LID_REACH = 2;

const lidCache = new WeakMap<LevelGeometry, Box[]>();

/**
 * Invisible ceilings over any floor that is open to the sky.
 *
 * The Great Cathedral has two — the Cloister and the Tabernacle, both lit by a
 * moonlight through the gap — and through them ghosts and Mentalists left the
 * building and flew about on the roof. The fix belongs in collision, not in the
 * blockout: the openings are the point, and the moon has to keep shining
 * through them. So a lid is never drawn and never added to `boxes`; the shared
 * collider builder in `net/levelColliders.ts` adds one wherever this says.
 *
 * A floor is open where a sample point on it has no ceiling over it and no wall
 * or pillar standing to the roof line. The roof line is the underside of the
 * ceilings round the hole — the lid sits flush with them, so a hole reads from
 * below as the ceiling it is in rather than as a lower one — or, on a map with
 * no ceiling near, the tallest wall round it. Open samples are merged into
 * rectangles and each is grown a little, so the lid overlaps the roof it plugs.
 *
 * Pure geometry, and deterministic: both worlds call it on the same boxes and
 * get the same lids. Cached per geometry, since every world built from a map
 * asks.
 */
export function skyLids(geometry: LevelGeometry): Box[] {
  const cached = lidCache.get(geometry);
  if (cached) return cached;

  const boxes = geometry.boxes;
  const floors = boxes.filter((b) => b.kind === 'floor');
  const ceilings = boxes.filter((b) => b.kind === 'ceiling');
  const uprights = boxes.filter((b) => b.kind === 'wall' || b.kind === 'pillar');

  const contains = (b: Box, x: number, z: number): boolean => {
    // Broad phase first: most boxes are nowhere near most samples.
    const f = boxFootprint(b);
    if (Math.abs(x - b.pos.x) > f.x || Math.abs(z - b.pos.z) > f.z) return false;
    const local = toBoxLocal(b, { x, y: b.pos.y, z });
    return Math.abs(local.x) <= b.size.x / 2 && Math.abs(local.z) <= b.size.z / 2;
  };
  const near = (b: Box, x0: number, x1: number, z0: number, z1: number): boolean => {
    const f = boxFootprint(b);
    return b.pos.x + f.x >= x0 && b.pos.x - f.x <= x1 && b.pos.z + f.z >= z0 && b.pos.z - f.z <= z1;
  };
  const top = (b: Box) => b.pos.y + b.size.y / 2;
  const bottom = (b: Box) => b.pos.y - b.size.y / 2;

  // Open cells, grouped by the height their lid wants: roof → "ix,iz".
  const open = new Map<number, Set<string>>();

  for (const f of floors) {
    const ff = boxFootprint(f);
    const x0 = f.pos.x - ff.x;
    const x1 = f.pos.x + ff.x;
    const z0 = f.pos.z - ff.z;
    const z1 = f.pos.z + ff.z;
    const floorTop = top(f);

    const roofs = ceilings.filter((c) => bottom(c) > floorTop && near(c, x0 - LID_REACH, x1 + LID_REACH, z0 - LID_REACH, z1 + LID_REACH));
    const walls = uprights.filter((w) => top(w) > floorTop && near(w, x0 - LID_REACH, x1 + LID_REACH, z0 - LID_REACH, z1 + LID_REACH));
    const roofLine = roofs.length
      ? Math.min(...roofs.map(bottom))
      : walls.length ? Math.max(...walls.map(top)) : null;
    // A floor with nothing round it at all is not a room; nothing to close.
    if (roofLine === null) continue;

    const covering = [
      ...roofs,
      ...walls.filter((w) => top(w) >= roofLine - 0.05),
    ];

    for (let ix = Math.floor(x0 / LID_CELL); ix * LID_CELL < x1; ix++) {
      for (let iz = Math.floor(z0 / LID_CELL); iz * LID_CELL < z1; iz++) {
        const x = (ix + 0.5) * LID_CELL;
        const z = (iz + 0.5) * LID_CELL;
        if (!contains(f, x, z)) continue;
        if (covering.some((c) => contains(c, x, z))) continue;
        const key = Math.round(roofLine * 1000) / 1000;
        let cells = open.get(key);
        if (!cells) open.set(key, (cells = new Set()));
        cells.add(`${ix},${iz}`);
      }
    }
  }

  const lids: Box[] = [];
  for (const [roof, cells] of [...open].sort((a, b) => a[0] - b[0])) {
    for (const r of mergeCells(cells)) {
      const w = (r.ix1 - r.ix0 + 1) * LID_CELL + LID_MARGIN * 2;
      const d = (r.iz1 - r.iz0 + 1) * LID_CELL + LID_MARGIN * 2;
      const cx = ((r.ix0 + r.ix1 + 1) / 2) * LID_CELL;
      const cz = ((r.iz0 + r.iz1 + 1) / 2) * LID_CELL;
      lids.push(box(cx, roof + LID_THICKNESS / 2, cz, w, LID_THICKNESS, d, 'ceiling', 'sky-lid'));
    }
  }

  lidCache.set(geometry, lids);
  return lids;
}

/**
 * Greedy rectangles over a set of grid cells: take the lowest unclaimed cell,
 * run as far along x as the set allows, then extend down z while every cell of
 * that run is present. Not minimal, and does not need to be — a lid is two
 * boxes or six, and either way nothing gets through.
 */
function mergeCells(cells: Set<string>): { ix0: number; ix1: number; iz0: number; iz1: number }[] {
  const left = new Set(cells);
  const parsed = [...cells].map((k) => k.split(',').map(Number) as [number, number])
    .sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  const rects: { ix0: number; ix1: number; iz0: number; iz1: number }[] = [];

  for (const [ix, iz] of parsed) {
    if (!left.has(`${ix},${iz}`)) continue;
    let ix1 = ix;
    while (left.has(`${ix1 + 1},${iz}`)) ix1++;
    let iz1 = iz;
    for (;;) {
      let full = true;
      for (let x = ix; x <= ix1; x++) if (!left.has(`${x},${iz1 + 1}`)) { full = false; break; }
      if (!full) break;
      iz1++;
    }
    for (let z = iz; z <= iz1; z++) for (let x = ix; x <= ix1; x++) left.delete(`${x},${z}`);
    rects.push({ ix0: ix, ix1, iz0: iz, iz1 });
  }
  return rects;
}

/**
 * The house grade: cold, foggy and lit almost entirely by fire.
 *
 * These are the numbers an arena inherits unless it says otherwise, and they are
 * the ones most worth playtesting — the whole readability question lives here.
 * The rule they encode is the one every dark shooter follows: keep the world
 * muted and low in value, and let the characters and the spells be the only
 * saturated things on screen.
 */
export const DEFAULT_LIGHTING: Required<LightingSettings> = {
  skyColor: 0x0a0b0e,
  // A shade cooler and lighter than the sky, so distance reads as depth rather
  // than as a brown wash. Fog is the single biggest influence on what a colour
  // looks like at range, and a warm fog turns every faction the same brown.
  fogColor: 0x14161c,
  fogDensity: 0.013,
  moonColor: 0x9fb2d0,
  // Raised from 0.55. This is the light that says what colour a thing *is*, and
  // it was quiet enough that eight saturated torches were saying it instead.
  moonIntensity: 1.05,
  // Was 0x2a1f14 at saturation 0.54 — a strong brown that every downward-facing
  // surface in the arena picked up. Warm still, but nearer neutral.
  bounceColor: 0x3a352e,
  bounceIntensity: 0.5,
  // Was fully saturated orange. A torch should look warm; it should not repaint
  // everything within its range. Colour comes from the light being warm, not
  // from it being pure.
  torchColor: 0xffc89a,
  torchFlicker: 0.14,
  torchFlickerSpeed: 5.5,
  shadows: true,
  exposure: 1.15,
};
